import React, { useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  CreditCardIcon,
  EyeIcon,
  UserPlusIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  ClockIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { useDataCache } from '../contexts/DataCacheContext';
import ConfirmActionDialog from '../components/ConfirmActionDialog';
import LoadingSpinner from '../components/LoadingSpinner';

const StudentManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [showRegistration, setShowRegistration] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailStudent, setDetailStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  // Course dropdown states
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const courseDropdownRef = useRef(null);

  // Edit course dropdown states
  const [editCourseSearchTerm, setEditCourseSearchTerm] = useState('');
  const [showEditCourseDropdown, setShowEditCourseDropdown] = useState(false);
  const [selectedEditCourse, setSelectedEditCourse] = useState(null);
  const editCourseDropdownRef = useRef(null);

  // Real student data from API
  const { students: cachedStudents, courses: cachedCourses, getStudents: cacheGetStudents, getCourses: cacheGetCourses, mutateStudents, prefetchCourses } = useDataCache();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);

  const [newStudent, setNewStudent] = useState({
    full_name: '',
    email: '',
    phone: '',
    gender: 'male',
    address: '',
    course: '',
    workplace: '',
    workplace_number: '',
    job_title: '',
    next_of_kin_name: '',
    next_of_kin_phone: '',
    payment_type: 'full',
    total_fee: '',
    amount_paid: '',
    payment_confirmed: false
  });

  const [editStudent, setEditStudent] = useState({
    full_name: '',
    email: '',
    phone: '',
    gender: 'male',
    address: '',
    course: '',
    workplace: '',
    workplace_number: '',
    job_title: '',
    next_of_kin_name: '',
    next_of_kin_phone: '',
    payment_type: 'full',
    total_fee: '',
    amount_paid: '',
    payment_confirmed: false
  });

  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [registrationCredentials, setRegistrationCredentials] = useState({
    email: '',
    password: ''
  });
  const [resetCredentials, setResetCredentials] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [sendResetEmail, setSendResetEmail] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  // Load data on component mount
  useEffect(() => {
    // Load from cache immediately, then refresh in background
    setStudents(Array.isArray(cachedStudents) ? cachedStudents : []);
    setCourses(Array.isArray(cachedCourses) ? cachedCourses : []);

    const run = async () => {
      setLoading(true);
      const s = await cacheGetStudents({ background: false });
      setStudents(Array.isArray(s) ? s : []);
      setLoading(false);

      setCoursesLoading(true);
      const c = await cacheGetCourses({ background: false });
      setCourses(Array.isArray(c) ? c : []);
      setCoursesLoading(false);
    };
    run();
  }, []);

  // Keep local views in sync when cache changes
  useEffect(() => {
    setStudents(Array.isArray(cachedStudents) ? cachedStudents : []);
  }, [cachedStudents]);
  useEffect(() => {
    setCourses(Array.isArray(cachedCourses) ? cachedCourses : []);
  }, [cachedCourses]);

  // Handle clicking outside course dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
        setShowCourseDropdown(false);
      }
      if (editCourseDropdownRef.current && !editCourseDropdownRef.current.contains(event.target)) {
        setShowEditCourseDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!detailStudent || !Array.isArray(students)) return;
    const refreshedStudent = students.find(student => student.id === detailStudent.id);
    if (refreshedStudent && refreshedStudent !== detailStudent) {
      setDetailStudent(refreshedStudent);
    }
  }, [students, detailStudent]);

  // Filter courses based on search term
  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
    (course.description && course.description.toLowerCase().includes(courseSearchTerm.toLowerCase()))
  );

  // Filter courses for edit modal
  const filteredEditCourses = courses.filter(course =>
    course.name.toLowerCase().includes(editCourseSearchTerm.toLowerCase()) ||
    (course.description && course.description.toLowerCase().includes(editCourseSearchTerm.toLowerCase()))
  );

  const getCourseById = (courseId) => {
    if (!courseId) return null;
    return courses.find(course => course.id === courseId) || null;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'Not available';
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return String(value);
    }

    try {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(numericValue);
    } catch (error) {
      console.warn('Failed to format currency:', error);
      return `₦${numericValue.toLocaleString()}`;
    }
  };

  const formatDateTime = (value) => {
    if (!value) return 'Not available';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Not available';
    }

    return date.toLocaleString('en-NG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAmountOutstanding = (student) => {
    if (!student) return null;
    const totalFee = Number(student.total_fee) || 0;
    const amountPaid = Number(student.amount_paid) || 0;
    const outstanding = totalFee - amountPaid;
    return outstanding > 0 ? outstanding : 0;
  };

  const getInitials = (name) => {
    if (!name) return 'ST';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return 'ST';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const capitalize = (value) => {
    if (!value || typeof value !== 'string') return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const handleViewDetails = (student) => {
    setDetailStudent(student);
    setResetCredentials(null);
    setSuccess('');
    setError('');
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailStudent(null);
    setResetCredentials(null);
  };

  const handleEditFromDetail = () => {
    if (!detailStudent) return;
    handleEditStudent(detailStudent);
    closeDetailModal();
  };

  const handleDeleteFromDetail = () => {
    if (!detailStudent) return;
    handleDeleteStudent(detailStudent);
    closeDetailModal();
  };

  const handleConfirmPaymentFromDetail = async () => {
    if (!detailStudent?.id) return;
    await handleConfirmPayment(detailStudent.id);
  };

  const handleResetPasswordFromDetail = async (sendEmail = false) => {
    if (!detailStudent?.id) return;
    setResettingPassword(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.resetStudentPassword(detailStudent.id, { send_email: sendEmail });
      const credentials = response?.login_credentials || {};
      const formattedCredentials = {
        email: credentials.email || detailStudent.email || '',
        password: credentials.password || '',
        login_url: credentials.login_url || '',
        student_id: credentials.student_id || detailStudent.login_id || ''
      };

      setResetCredentials(formattedCredentials);
      setSuccess(sendEmail ? 'Password reset and emailed to student.' : 'Password reset successfully.');
    } catch (error) {
      console.error('Failed to reset student password:', error);
      const errorMessage = error?.message ? `Password reset failed: ${error.message}` : 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setResettingPassword(false);
    }
    setShowResetConfirm(false);
  };

  const handleResetPasswordPrompt = () => {
    if (!detailStudent) return;
    setSendResetEmail(false);
    setShowResetConfirm(true);
  };

  const handleCopyRegistrationCredentials = async () => {
    const { email, password } = registrationCredentials;
    if (!email && !password) return;

    try {
      const text = `Student Login Credentials\nEmail: ${email}\nPassword: ${password}`;
      await navigator.clipboard.writeText(text);
      setCopyFeedback('Credentials copied to clipboard');
      setTimeout(() => setCopyFeedback(''), 2500);
    } catch (error) {
      console.error('Failed to copy credentials:', error);
      setCopyFeedback('Copy failed. Select and copy manually.');
      setTimeout(() => setCopyFeedback(''), 2500);
    }
  };

  const closeRegistrationSuccessDialog = () => {
    setShowSuccessDialog(false);
    setCopyFeedback('');
    setRegistrationCredentials({ email: '', password: '' });
  };

  // Handle course selection
  const handleCourseSelect = (course) => {
    const courseFee = course?.cost_amount ?? course?.total_fee ?? course?.price ?? '';
    setSelectedCourse(course);
    setNewStudent({
      ...newStudent,
      course: course.id,
      total_fee: courseFee !== '' ? String(courseFee) : ''
    });
    setShowCourseDropdown(false);
    setCourseSearchTerm('');
  };

  // Handle edit course selection
  const handleEditCourseSelect = (course) => {
    const courseFee = course?.cost_amount ?? course?.total_fee ?? course?.price ?? '';
    setSelectedEditCourse(course);
    setEditStudent({
      ...editStudent,
      course: course.id,
      total_fee: courseFee !== '' ? String(courseFee) : editStudent.total_fee
    });
    setShowEditCourseDropdown(false);
    setEditCourseSearchTerm('');
  };

  const loadStudents = async () => {
    try {
      setLoading(true);
      const response = await cacheGetStudents({ background: false });
      
      // Handle paginated response structure
      let studentsData;
      if (response && response.results && Array.isArray(response.results)) {
        // Paginated response
        studentsData = response.results;
      } else if (Array.isArray(response)) {
        // Direct array response
        studentsData = response;
      } else {
        // Fallback to empty array
        studentsData = [];
      }
      
      setStudents(studentsData);
      mutateStudents(studentsData);
      setError('');
    } catch (error) {
      console.error('Failed to load students:', error);
      setError('Failed to load students. Please try again.');
      setStudents([]); // Ensure students is an array even on error
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    console.log('1. Starting loadCourses, setting loading to true');
    setCoursesLoading(true);
    
    try {
      console.log('2. Making API call to get ALL courses...');
      // Use the API service method which handles pagination and fetches ALL courses
      const allCourses = await cacheGetCourses({ background: false });
      console.log('3. API data received:', allCourses);
      
      if (Array.isArray(allCourses)) {
        setCourses(allCourses);
        console.log('4. Set courses:', allCourses.length, 'courses loaded successfully');
      } else {
        setCourses([]);
        console.log('4. No courses found in response');
      }
      setError('');
    } catch (error) {
      console.log('API error caught:', error);
      setError('Failed to load courses. Please try again.');
      setCourses([]);
    }
    
    console.log('5. Setting loading to false');
    setCoursesLoading(false);
  };

  const tabs = [
    { id: 'all', name: 'All Students', count: Array.isArray(students) ? students.length : 0 },
    { id: 'paid', name: 'Paid', count: Array.isArray(students) ? students.filter(s => s.payment_confirmed).length : 0 },
    { id: 'pending', name: 'Pending Payment', count: Array.isArray(students) ? students.filter(s => !s.payment_confirmed).length : 0 },
  ];

  const handleRegisterStudent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Validate that a course is selected
    if (!newStudent.course) {
      setError('Please select a course for the student.');
      setLoading(false);
      return;
    }
    
    try {
      console.log('Registering student with data:', newStudent);
      const response = await apiService.createStudent(newStudent);
      await loadStudents(); // Reload students list
      
      // Reset form
      setNewStudent({ full_name: '', email: '', phone: '', gender: 'male', address: '', course: '', workplace: '', workplace_number: '', job_title: '', next_of_kin_name: '', next_of_kin_phone: '', payment_type: 'full', total_fee: '', amount_paid: '', payment_confirmed: false });
      setSelectedCourse(null);
      setCourseSearchTerm('');
      setShowCourseDropdown(false);
      setShowRegistration(false);
      
      // Show success dialog with credentials
      const credentials = response?.login_credentials || {};
      setRegistrationCredentials({
        email: credentials.email || newStudent.email,
        password: credentials.password || ''
      });
      setShowSuccessDialog(true);
      setSuccess('');
      setError('');
    } catch (error) {
      console.error('Failed to register student - Full error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Show more specific error message if available
      let errorMessage = 'Failed to register student. Please try again.';
      if (error.message) {
        errorMessage = `Registration failed: ${error.message}`;
      }
      
      setError(errorMessage);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async (studentId) => {
    try {
      await apiService.confirmStudentPayment(studentId);
      await loadStudents(); // Reload students list
      setSuccess('Payment confirmed successfully!');
      setError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      setError('Failed to confirm payment. Please try again.');
      setSuccess('');
    }
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setEditStudent({
      full_name: student.full_name || '',
      email: student.email || '',
      phone: student.phone || '',
      gender: student.gender || 'male',
      address: student.address || '',
      course: student.course || '',
      workplace: student.workplace || '',
      workplace_number: student.workplace_number || '',
      job_title: student.job_title || '',
      next_of_kin_name: student.next_of_kin_name || '',
      next_of_kin_phone: student.next_of_kin_phone || '',
      payment_type: student.payment_type || 'full',
      total_fee: student.total_fee || '',
      amount_paid: student.amount_paid || '',
      payment_confirmed: student.payment_confirmed || false
    });
    
    // Find and set the selected course for editing
    const studentCourse = courses.find(course => course.id === student.course);
    setSelectedEditCourse(studentCourse || null);
    setEditCourseSearchTerm('');
    setShowEditCourseDropdown(false);
    
    setShowEditModal(true);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Validate that a course is selected
    if (!editStudent.course) {
      setError('Please select a course for the student.');
      setLoading(false);
      return;
    }
    
    try {
      console.log('Updating student with data:', editStudent);
      await apiService.updateStudent(selectedStudent.id, editStudent);
      await loadStudents(); // Reload students list
      
      // Reset states
      setShowEditModal(false);
      setSelectedStudent(null);
      setSelectedEditCourse(null);
      setEditCourseSearchTerm('');
      setShowEditCourseDropdown(false);
      setSuccess('Student updated successfully!');
      setError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update student - Full error:', error);
      console.error('Error message:', error.message);
      
      // Show more specific error message if available
      let errorMessage = 'Failed to update student. Please try again.';
      if (error.message) {
        errorMessage = `Update failed: ${error.message}`;
      }
      
      setError(errorMessage);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = (student) => {
    setSelectedStudent(student);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteStudent = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await apiService.deleteStudent(selectedStudent.id);
      await loadStudents(); // Reload students list
      setShowDeleteConfirm(false);
      setSelectedStudent(null);
      setSuccess('Student deleted successfully!');
      setError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to delete student:', error);
      setError('Failed to delete student. Please try again.');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = Array.isArray(students) ? students.filter(student => {
    const matchesSearch = student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.login_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'paid' && student.payment_confirmed) ||
                      (activeTab === 'pending' && !student.payment_confirmed);
                      
    return matchesSearch && matchesTab;
  }) : [];

  const detailCourse = detailStudent ? getCourseById(detailStudent.course) : null;
  const outstandingAmount = detailStudent ? getAmountOutstanding(detailStudent) : null;

  const getPaymentStatusColor = (paymentConfirmed) => {
    return paymentConfirmed 
      ? 'bg-green-100 text-green-800'
      : 'bg-yellow-100 text-yellow-800';
  };

  const getPaymentStatusText = (paymentConfirmed) => {
    return paymentConfirmed ? 'Paid' : 'Pending';
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Notifications */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-900 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-white/20">
              <UserPlusIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-white opacity-90">Total Students</p>
              <p className="text-2xl font-bold text-white">{Array.isArray(students) ? students.length : 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-white/20">
              <CheckCircleIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-white opacity-90">Active Students</p>
              <p className="text-2xl font-bold text-white">
                {Array.isArray(students) ? students.filter(s => s.status === 'Active').length : 0}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-700 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-white/20">
              <BanknotesIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-white opacity-90">Pending Payments</p>
              <p className="text-2xl font-bold text-white">
                {Array.isArray(students) ? students.filter(s => !s.payment_confirmed).length : 0}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-900 rounded-xl p-6 shadow-lg">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-white/20">
              <XCircleIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-white opacity-90">Overdue</p>
              <p className="text-2xl font-bold text-white">
                {Array.isArray(students) ? students.filter(s => s.is_overdue).length : 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Students Table */}
        <div className="flex-1 space-y-6">
          {/* Students Table */}
          <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-300">Student</th>
                <th className="text-left py-3 px-4 font-medium text-gray-300">Email</th>
                <th className="text-left py-3 px-4 font-medium text-gray-300">Phone</th>
                <th className="text-left py-3 px-4 font-medium text-gray-300">Gender</th>
                <th className="text-left py-3 px-4 font-medium text-gray-300">Payment</th>
                <th className="text-left py-3 px-4 font-medium text-gray-300">Created</th>
                <th className="text-left py-3 px-4 font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center">
                    <div className="flex items-center justify-center">
                      <LoadingSpinner size="medium" message="Loading students..." />
                    </div>
                  </td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-400">
                    No students found. {students.length === 0 ? 'Register your first student to get started!' : 'Try adjusting your search or filters.'}
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student, index) => (
                  <tr
                    key={student.id}
                    onClick={() => handleViewDetails(student)}
                    className={`${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-900'} hover:bg-gray-700/50 transition-colors cursor-pointer`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-3">
                          <span className="text-white text-xs font-medium">
                            {student.full_name ? student.full_name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'ST'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white text-sm">{student.full_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-300">{student.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-300">{student.phone}</td>
                    <td className="py-3 px-4 text-sm text-gray-300 capitalize">{student.gender}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(student.payment_confirmed)}`}>
                        {getPaymentStatusText(student.payment_confirmed)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-300">
                      {new Date(student.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        {!student.payment_confirmed && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfirmPayment(student.id);
                            }}
                            className="p-1 text-green-400 hover:text-green-300"
                            title="Confirm Payment"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(student);
                          }}
                          className="p-1 text-blue-400 hover:text-blue-300" 
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditStudent(student);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-300" 
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStudent(student);
                          }}
                          className="p-1 text-red-400 hover:text-red-300" 
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>
        </div>

        {/* Right Sidebar - Quick Actions and Filters */}
        <div className="w-72 space-y-6 flex-shrink-0">
          {/* Quick Actions */}
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white">Quick Actions</h3>
                <span className="bg-gray-700 text-white text-xs font-semibold px-2 py-0.5 rounded-full">2</span>
              </div>
            </div>
            <div className="space-y-2">
              <button 
                onClick={() => {
                  setShowRegistration(true);
                  setSelectedCourse(null);
                  setCourseSearchTerm('');
                  setShowCourseDropdown(false);
                }}
                className="w-full bg-gray-700 border border-orange-900/50 rounded-lg p-3 hover:bg-gray-600 transition-colors flex items-center gap-3 group"
              >
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <UserPlusIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">Register Student</p>
                  <p className="text-xs text-blue-400 mt-0.5">Add New Student</p>
                </div>
              </button>
              
              <button className="w-full bg-gray-800 rounded-lg p-3 hover:bg-gray-700 transition-colors flex items-center gap-3 group">
                <div className="w-10 h-10 border-2 border-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <CreditCardIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">Confirm Payments</p>
                  <p className="text-xs text-blue-400 mt-0.5">Payment Processing</p>
                </div>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-gray-800 rounded-xl p-4">
            <h3 className="text-base font-bold text-white mb-4">Filters</h3>
            
            {/* Tabs */}
            <div className="mb-4">
              <div className="flex flex-col gap-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                      activeTab === tab.id
                        ? 'bg-gray-700 text-white shadow-sm'
                        : 'bg-gray-900 text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    {tab.name} ({tab.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="space-y-3">
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 w-full bg-gray-900 border border-gray-700 text-white text-sm placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button className="w-full bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center">
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Success Dialog */}
      {showSuccessDialog && (
        <div className="fixed inset-0 z-[998] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white">
              <button
                onClick={closeRegistrationSuccessDialog}
                className="absolute right-6 top-6 text-white/70 transition hover:text-white"
                aria-label="Close credentials dialog"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>

              <div className="flex flex-col gap-3 pr-10">
                <p className="text-xs uppercase tracking-[0.35em] text-white/70">Student Credentials</p>
                <h2 className="text-2xl font-semibold leading-tight">Student registered successfully</h2>
                <p className="text-sm text-white/80 max-w-xl">
                  Share these credentials securely with the student. Encourage them to sign in and update their password immediately.
                </p>
              </div>
            </div>

            <div className="space-y-5 p-6">
              <dl className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="grid grid-cols-1 gap-2 px-4 py-3 sm:grid-cols-12 sm:items-center">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 sm:col-span-3">Email</dt>
                  <dd className="text-sm font-semibold text-gray-900 break-words sm:col-span-9">{registrationCredentials.email || 'Not available'}</dd>
                </div>
                <div className="grid grid-cols-1 gap-2 px-4 py-3 sm:grid-cols-12 sm:items-center">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 sm:col-span-3">Password</dt>
                  <dd className="text-sm font-semibold text-gray-900 sm:col-span-9">{registrationCredentials.password || 'Not available'}</dd>
                </div>
              </dl>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                <p className="flex items-start gap-2">
                  <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-indigo-500" />
                  <span>
                    The credentials display only once on registration. Copy and store them securely, or send a password reset later if needed.
                  </span>
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-medium text-indigo-600">{copyFeedback}</div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleCopyRegistrationCredentials}
                    className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500"
                  >
                    <KeyIcon className="mr-2 h-4 w-4" />
                    Copy Credentials
                  </button>
                  <button
                    type="button"
                    onClick={closeRegistrationSuccessDialog}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student Detail Modal */}
      {showDetailModal && detailStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl border border-gray-200">
            <div className="relative">
              <div className="bg-gradient-to-r from-blue-900 via-indigo-800 to-purple-800 p-6 pb-8 text-white">
                <button
                  onClick={closeDetailModal}
                  className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
                  aria-label="Close detail view"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pr-10">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-white/15 border border-white/30 flex items-center justify-center text-2xl font-semibold">
                      {getInitials(detailStudent.full_name)}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.35em] text-white/60">Student Profile</p>
                      <h2 className="text-2xl font-semibold leading-tight">{detailStudent.full_name || 'Unnamed Student'}</h2>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-white/10 border border-white/20">
                          <ShieldCheckIcon className="h-4 w-4" />
                          {getPaymentStatusText(detailStudent.payment_confirmed)}
                        </span>
                        {detailStudent.payment_type && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-white/10 border border-white/20">
                            <CreditCardIcon className="h-4 w-4" />
                            {capitalize(detailStudent.payment_type)} Payment
                          </span>
                        )}
                        {detailStudent.login_id && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-white/10 border border-white/20">
                            <IdentificationIcon className="h-4 w-4" />
                            {detailStudent.login_id}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start md:items-end gap-2 text-sm text-white/80">
                    <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1">
                      <CalendarDaysIcon className="h-4 w-4" />
                      <span>Joined {formatDateTime(detailStudent.created_at)}</span>
                    </div>
                    {detailCourse?.category && (
                      <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1">
                        <AcademicCapIcon className="h-4 w-4" />
                        <span>{capitalize(detailCourse.category)} Track</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-white">
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                    <div className="flex items-center gap-3">
                      <span className="p-2 rounded-lg bg-blue-100 text-blue-700">
                        <CalendarDaysIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Enrollment Date</p>
                        <p className="text-sm font-semibold text-blue-900 mt-1">{formatDateTime(detailStudent.created_at)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                    <div className="flex items-center gap-3">
                      <span className="p-2 rounded-lg bg-indigo-100 text-indigo-700">
                        <AcademicCapIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Course</p>
                        <p className="text-sm font-semibold text-indigo-900 mt-1">{detailCourse?.name || detailStudent.course_name || 'Not assigned'}</p>
                        {detailCourse?.duration_months && (
                          <p className="text-xs text-indigo-600 mt-1">{detailCourse.duration_months} months</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                    <div className="flex items-center gap-3">
                      <span className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
                        <BanknotesIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Outstanding Balance</p>
                        <p className="text-sm font-semibold text-emerald-900 mt-1">{outstandingAmount !== null ? formatCurrency(outstandingAmount) : 'Not available'}</p>
                        <p className="text-xs text-emerald-600 mt-1">Paid {formatCurrency(detailStudent.amount_paid)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {resetCredentials?.password && (
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <span className="p-2 rounded-lg bg-white/70 text-indigo-600">
                          <ShieldCheckIcon className="h-5 w-5" />
                        </span>
                        <div>
                          <h4 className="text-sm font-semibold text-indigo-900">Temporary Credentials Generated</h4>
                          <p className="text-xs text-indigo-700">Share these one-time credentials securely with the student. Encourage them to update their password after signing in.</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg border border-indigo-100 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">Email</p>
                        <p className="text-sm font-semibold text-indigo-900 mt-1 break-all">{resetCredentials.email || 'Not available'}</p>
                      </div>
                      <div className="bg-white rounded-lg border border-indigo-100 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">Temporary Password</p>
                        <p className="text-sm font-semibold text-indigo-900 mt-1">{resetCredentials.password || 'Not available'}</p>
                      </div>
                      {resetCredentials.student_id && (
                        <div className="bg-white rounded-lg border border-indigo-100 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">Student ID</p>
                          <p className="text-sm font-semibold text-indigo-900 mt-1">{resetCredentials.student_id}</p>
                        </div>
                      )}
                    </div>
                    {resetCredentials.login_url && (
                      <div className="mt-3 text-xs text-indigo-700">
                        Login URL: <span className="font-semibold">{resetCredentials.login_url}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="p-2 rounded-lg bg-gray-100 text-gray-700">
                          <UserIcon className="h-5 w-5" />
                        </span>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Basic Information</h4>
                          <p className="text-xs text-gray-500">Core personal details</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <UserIcon className="h-4 w-4" />
                            Full Name
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.full_name || 'Not provided'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <UserIcon className="h-4 w-4" />
                            Gender
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.gender ? capitalize(detailStudent.gender) : 'Not provided'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <EnvelopeIcon className="h-4 w-4" />
                            Email
                          </div>
                          <p className="text-sm font-semibold text-gray-900 break-words mt-1">{detailStudent.email || 'Not provided'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <PhoneIcon className="h-4 w-4" />
                            Phone
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.phone || 'Not provided'}</p>
                        </div>
                        <div className="sm:col-span-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <MapPinIcon className="h-4 w-4" />
                            Address
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1 whitespace-pre-line">{detailStudent.address || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="p-2 rounded-lg bg-gray-100 text-gray-700">
                          <AcademicCapIcon className="h-5 w-5" />
                        </span>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Enrollment & Access</h4>
                          <p className="text-xs text-gray-500">Course placement and credentials</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <AcademicCapIcon className="h-4 w-4" />
                            Course
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailCourse?.name || detailStudent.course_name || 'Not assigned'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <AcademicCapIcon className="h-4 w-4" />
                            Course Duration
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailCourse?.duration_months ? `${detailCourse.duration_months} months` : 'Not available'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <IdentificationIcon className="h-4 w-4" />
                            Login ID
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.login_id || 'Not generated yet'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <ClockIcon className="h-4 w-4" />
                            Last Updated
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{formatDateTime(detailStudent.updated_at || detailStudent.modified_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="p-2 rounded-lg bg-gray-100 text-gray-700">
                          <BuildingOfficeIcon className="h-5 w-5" />
                        </span>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Work Information</h4>
                          <p className="text-xs text-gray-500">Professional background (if provided)</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <BuildingOfficeIcon className="h-4 w-4" />
                            Workplace
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.workplace || 'Not provided'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <BriefcaseIcon className="h-4 w-4" />
                            Job Title
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.job_title || 'Not provided'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <PhoneIcon className="h-4 w-4" />
                            Workplace Phone
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.workplace_number || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="p-2 rounded-lg bg-gray-100 text-gray-700">
                          <UserIcon className="h-5 w-5" />
                        </span>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Emergency Contact</h4>
                          <p className="text-xs text-gray-500">Next of kin details</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <UserIcon className="h-4 w-4" />
                            Contact Name
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.next_of_kin_name || 'Not provided'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <PhoneIcon className="h-4 w-4" />
                            Contact Phone
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{detailStudent.next_of_kin_phone || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="p-2 rounded-lg bg-gray-100 text-gray-700">
                          <CreditCardIcon className="h-5 w-5" />
                        </span>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Payment Overview</h4>
                          <p className="text-xs text-gray-500">Fee structure and receipts</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <CreditCardIcon className="h-4 w-4" />
                            Total Fee
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{formatCurrency(detailStudent.total_fee)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <ShieldCheckIcon className="h-4 w-4" />
                            Amount Paid
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{formatCurrency(detailStudent.amount_paid)}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <BanknotesIcon className="h-4 w-4" />
                            Outstanding
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{outstandingAmount !== null ? formatCurrency(outstandingAmount) : 'Not available'}</p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            <ShieldCheckIcon className="h-4 w-4" />
                            Payment Status
                          </div>
                          <span className={`inline-flex mt-2 px-2.5 py-1 text-xs font-semibold rounded-full ${detailStudent.payment_confirmed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {getPaymentStatusText(detailStudent.payment_confirmed)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handleEditFromDetail}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors disabled:opacity-60"
                      disabled={loading}
                    >
                      <PencilIcon className="h-4 w-4" />
                      Edit Student
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPasswordPrompt}
                      className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors disabled:opacity-60"
                      disabled={loading || resettingPassword}
                    >
                      <KeyIcon className="h-4 w-4" />
                      {resettingPassword ? 'Resetting...' : 'Reset Login Password'}
                    </button>
                    {!detailStudent.payment_confirmed && (
                      <button
                        type="button"
                        onClick={handleConfirmPaymentFromDetail}
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 transition-colors disabled:opacity-60"
                        disabled={loading}
                      >
                        <ShieldCheckIcon className="h-4 w-4" />
                        Confirm Payment
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleDeleteFromDetail}
                      className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors disabled:opacity-60"
                      disabled={loading}
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete Student
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={closeDetailModal}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <XCircleIcon className="h-4 w-4" />
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmActionDialog
        isOpen={showResetConfirm}
        onCancel={() => {
          if (!resettingPassword) {
            setShowResetConfirm(false);
            setSendResetEmail(false);
          }
        }}
        onConfirm={() => handleResetPasswordFromDetail(sendResetEmail)}
        onSecondary={() => {
          setSendResetEmail((prev) => !prev);
        }}
        confirmLabel={sendResetEmail ? 'Reset & Send Email' : 'Reset Password'}
        secondaryLabel={sendResetEmail ? 'Email Enabled' : 'Email Student'}
        showSecondaryAction
        loading={resettingPassword}
        title="Reset student password?"
        description="This will generate a fresh temporary password for the student and invalidate the current one immediately."
        confirmVariant="info"
        icon={<KeyIcon />}
        iconClassName=""
      >
        <ul className="list-disc space-y-1 pl-5">
          <li>The new password will display below once generated.</li>
          <li>Share the credentials securely with the student.</li>
          <li>Encourage the student to change it after logging in.</li>
          <li>Email delivery is optional—you can toggle it above.</li>
        </ul>
      </ConfirmActionDialog>

      {/* Student Registration Modal */}
      {showRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header - Fixed */}
            <div className="flex-shrink-0 p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Register New Student</h3>
                <button
                  onClick={() => {
                    setShowRegistration(false);
                    setSelectedCourse(null);
                    setCourseSearchTerm('');
                    setShowCourseDropdown(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-2" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}
          
            <form onSubmit={handleRegisterStudent} className="space-y-6">
              {/* Basic Information Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={newStudent.full_name}
                      onChange={(e) => setNewStudent({...newStudent, full_name: e.target.value})}
                      className="input-field"
                      placeholder="Enter student's full name"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={newStudent.email}
                      onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                      className="input-field"
                      placeholder="student@example.com"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={newStudent.phone}
                      onChange={(e) => setNewStudent({...newStudent, phone: e.target.value})}
                      className="input-field"
                      placeholder="e.g., 08123456789"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                    <select
                      required
                      value={newStudent.gender}
                      onChange={(e) => setNewStudent({...newStudent, gender: e.target.value})}
                      className="input-field"
                      disabled={loading}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                    <textarea
                      required
                      value={newStudent.address}
                      onChange={(e) => setNewStudent({...newStudent, address: e.target.value})}
                      className="input-field"
                      placeholder="Enter student's full address"
                      rows="2"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Course Selection Section */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Course Enrollment</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Course *</label>
                    <div className="relative" ref={courseDropdownRef}>
                      {/* Selected Course Display / Search Input */}
                      <div
                        className={`input-field cursor-pointer flex items-center justify-between ${
                          loading || coursesLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={() => !loading && !coursesLoading && setShowCourseDropdown(!showCourseDropdown)}
                      >
                        <div className="flex-1">
                          {selectedCourse ? (
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-900">{selectedCourse.name}</span>
                                {selectedCourse.duration_months && (
                                  <span className="text-sm text-gray-500 ml-2">
                                    ({selectedCourse.duration_months} months)
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">
                              {coursesLoading ? 'Loading courses...' : 
                               courses.length === 0 ? 'No courses available' : 
                               'Search and select a course...'}
                            </span>
                          )}
                        </div>
                        {!loading && !coursesLoading && (
                          <div className="ml-2 flex-shrink-0">
                            {showCourseDropdown ? (
                              <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Dropdown */}
                      {showCourseDropdown && !loading && !coursesLoading && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                          {/* Search Input */}
                          <div className="p-3 border-b border-gray-200">
                            <div className="relative">
                              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search courses..."
                                value={courseSearchTerm}
                                onChange={(e) => setCourseSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                autoFocus
                              />
                            </div>
                          </div>

                          {/* Course List */}
                          <div className="max-h-64 overflow-y-auto">
                            {filteredCourses.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">
                                {courseSearchTerm ? 'No courses found matching your search' : 'No courses available'}
                              </div>
                            ) : (
                              filteredCourses.map((course) => (
                                <div
                                  key={course.id}
                                  className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                  onClick={() => handleCourseSelect(course)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <h5 className="font-medium text-gray-900 text-sm">{course.name}</h5>
                                      {course.duration_months && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Duration: {course.duration_months} months
                                        </p>
                                      )}
                                      {course.description && (
                                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                          {course.description.substring(0, 100)}...
                                        </p>
                                      )}
                                    </div>
                                    {selectedCourse && selectedCourse.id === course.id && (
                                      <CheckCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 ml-2" />
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Course Count */}
                          <div className="p-2 bg-gray-50 border-t border-gray-200">
                            <p className="text-xs text-gray-500 text-center">
                              {filteredCourses.length === courses.length 
                                ? `${courses.length} courses available` 
                                : `${filteredCourses.length} of ${courses.length} courses`}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Course Details */}
                    {selectedCourse && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{selectedCourse.name}</h5>
                            {selectedCourse.duration_months && (
                              <p className="text-sm text-gray-600 mt-1">
                                Duration: {selectedCourse.duration_months} months
                              </p>
                            )}
                            {selectedCourse.description && (
                              <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                                {selectedCourse.description}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCourse(null);
                              setNewStudent({...newStudent, course: ''});
                            }}
                            className="ml-3 p-1 text-gray-400 hover:text-red-500"
                            title="Remove selected course"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Work Information Section */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Work Information (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Workplace/Company</label>
                    <input
                      type="text"
                      value={newStudent.workplace}
                      onChange={(e) => setNewStudent({...newStudent, workplace: e.target.value})}
                      className="input-field"
                      placeholder="Company name or N/A if unemployed"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                    <input
                      type="text"
                      value={newStudent.job_title}
                      onChange={(e) => setNewStudent({...newStudent, job_title: e.target.value})}
                      className="input-field"
                      placeholder="Current job title"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Workplace Phone</label>
                    <input
                      type="tel"
                      value={newStudent.workplace_number}
                      onChange={(e) => setNewStudent({...newStudent, workplace_number: e.target.value})}
                      className="input-field"
                      placeholder="Workplace contact number"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Next of Kin Section */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Emergency Contact (Next of Kin)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Name *</label>
                    <input
                      type="text"
                      required
                      value={newStudent.next_of_kin_name}
                      onChange={(e) => setNewStudent({...newStudent, next_of_kin_name: e.target.value})}
                      className="input-field"
                      placeholder="Full name of emergency contact"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Phone *</label>
                    <input
                      type="tel"
                      required
                      value={newStudent.next_of_kin_phone}
                      onChange={(e) => setNewStudent({...newStudent, next_of_kin_phone: e.target.value})}
                      className="input-field"
                      placeholder="Emergency contact phone number"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Information Section */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Payment Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                    <select
                      value={newStudent.payment_type}
                      onChange={(e) => setNewStudent({...newStudent, payment_type: e.target.value})}
                      className="input-field"
                      disabled={loading}
                    >
                      <option value="full">Full Payment</option>
                      <option value="installment">Installment Payment</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Course Fee (₦)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newStudent.total_fee}
                      onChange={(e) => setNewStudent({...newStudent, total_fee: e.target.value})}
                      className="input-field"
                      placeholder="0.00"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid (₦)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newStudent.amount_paid}
                      onChange={(e) => setNewStudent({...newStudent, amount_paid: e.target.value})}
                      className="input-field"
                      placeholder="0.00"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="payment_confirmed"
                      checked={newStudent.payment_confirmed}
                      onChange={(e) => setNewStudent({...newStudent, payment_confirmed: e.target.checked})}
                      className="mr-2"
                      disabled={loading}
                    />
                    <label htmlFor="payment_confirmed" className="text-sm font-medium text-gray-700">
                      Payment Confirmed
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 btn-primary disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Registering...' : 'Register Student'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRegistration(false);
                    setSelectedCourse(null);
                    setCourseSearchTerm('');
                    setShowCourseDropdown(false);
                  }}
                  className="flex-1 btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Student Edit Modal */}
      {showEditModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header - Fixed */}
            <div className="flex-shrink-0 p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit Student - {selectedStudent.full_name}</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEditCourse(null);
                    setEditCourseSearchTerm('');
                    setShowEditCourseDropdown(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    <p className="text-sm text-green-800">{success}</p>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-2" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}
            
            <form onSubmit={handleUpdateStudent} className="space-y-6">
              {/* Basic Information Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={editStudent.full_name}
                      onChange={(e) => setEditStudent({...editStudent, full_name: e.target.value})}
                      className="input-field"
                      placeholder="Enter student's full name"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={editStudent.email}
                      onChange={(e) => setEditStudent({...editStudent, email: e.target.value})}
                      className="input-field"
                      placeholder="student@example.com"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={editStudent.phone}
                      onChange={(e) => setEditStudent({...editStudent, phone: e.target.value})}
                      className="input-field"
                      placeholder="e.g., 08123456789"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                    <select
                      required
                      value={editStudent.gender}
                      onChange={(e) => setEditStudent({...editStudent, gender: e.target.value})}
                      className="input-field"
                      disabled={loading}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                    <textarea
                      required
                      value={editStudent.address}
                      onChange={(e) => setEditStudent({...editStudent, address: e.target.value})}
                      className="input-field"
                      placeholder="Enter student's full address"
                      rows="2"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Course Selection Section */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Course Enrollment</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Course *</label>
                    <div className="relative" ref={editCourseDropdownRef}>
                      {/* Selected Course Display / Search Input */}
                      <div
                        className={`input-field cursor-pointer flex items-center justify-between ${
                          loading || coursesLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={() => !loading && !coursesLoading && setShowEditCourseDropdown(!showEditCourseDropdown)}
                      >
                        <div className="flex-1">
                          {selectedEditCourse ? (
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-900">{selectedEditCourse.name}</span>
                                {selectedEditCourse.duration_months && (
                                  <span className="text-sm text-gray-500 ml-2">
                                    ({selectedEditCourse.duration_months} months)
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">
                              {coursesLoading ? 'Loading courses...' : 
                               courses.length === 0 ? 'No courses available' : 
                               'Search and select a course...'}
                            </span>
                          )}
                        </div>
                        {!loading && !coursesLoading && (
                          <div className="ml-2 flex-shrink-0">
                            {showEditCourseDropdown ? (
                              <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Dropdown */}
                      {showEditCourseDropdown && !loading && !coursesLoading && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                          {/* Search Input */}
                          <div className="p-3 border-b border-gray-200">
                            <div className="relative">
                              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search courses..."
                                value={editCourseSearchTerm}
                                onChange={(e) => setEditCourseSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                autoFocus
                              />
                            </div>
                          </div>

                          {/* Course List */}
                          <div className="max-h-64 overflow-y-auto">
                            {filteredEditCourses.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">
                                {editCourseSearchTerm ? 'No courses found matching your search' : 'No courses available'}
                              </div>
                            ) : (
                              filteredEditCourses.map((course) => (
                                <div
                                  key={course.id}
                                  className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                  onClick={() => handleEditCourseSelect(course)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <h5 className="font-medium text-gray-900 text-sm">{course.name}</h5>
                                      {course.duration_months && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Duration: {course.duration_months} months
                                        </p>
                                      )}
                                      {course.description && (
                                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                          {course.description.substring(0, 100)}...
                                        </p>
                                      )}
                                    </div>
                                    {selectedEditCourse && selectedEditCourse.id === course.id && (
                                      <CheckCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 ml-2" />
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Course Count */}
                          <div className="p-2 bg-gray-50 border-t border-gray-200">
                            <p className="text-xs text-gray-500 text-center">
                              {filteredEditCourses.length === courses.length 
                                ? `${courses.length} courses available` 
                                : `${filteredEditCourses.length} of ${courses.length} courses`}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Course Details */}
                    {selectedEditCourse && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{selectedEditCourse.name}</h5>
                            {selectedEditCourse.duration_months && (
                              <p className="text-sm text-gray-600 mt-1">
                                Duration: {selectedEditCourse.duration_months} months
                              </p>
                            )}
                            {selectedEditCourse.description && (
                              <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                                {selectedEditCourse.description}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEditCourse(null);
                              setEditStudent({...editStudent, course: ''});
                            }}
                            className="ml-3 p-1 text-gray-400 hover:text-red-500"
                            title="Remove selected course"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Work Information Section */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Work Information (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Workplace/Company</label>
                    <input
                      type="text"
                      value={editStudent.workplace}
                      onChange={(e) => setEditStudent({...editStudent, workplace: e.target.value})}
                      className="input-field"
                      placeholder="Company name or N/A if unemployed"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                    <input
                      type="text"
                      value={editStudent.job_title}
                      onChange={(e) => setEditStudent({...editStudent, job_title: e.target.value})}
                      className="input-field"
                      placeholder="Current job title"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Workplace Phone</label>
                    <input
                      type="tel"
                      value={editStudent.workplace_number}
                      onChange={(e) => setEditStudent({...editStudent, workplace_number: e.target.value})}
                      className="input-field"
                      placeholder="Workplace contact number"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Next of Kin Section */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Emergency Contact (Next of Kin)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Name *</label>
                    <input
                      type="text"
                      required
                      value={editStudent.next_of_kin_name}
                      onChange={(e) => setEditStudent({...editStudent, next_of_kin_name: e.target.value})}
                      className="input-field"
                      placeholder="Full name of emergency contact"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Phone *</label>
                    <input
                      type="tel"
                      required
                      value={editStudent.next_of_kin_phone}
                      onChange={(e) => setEditStudent({...editStudent, next_of_kin_phone: e.target.value})}
                      className="input-field"
                      placeholder="Emergency contact phone number"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Information Section */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Payment Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                    <select
                      value={editStudent.payment_type}
                      onChange={(e) => setEditStudent({...editStudent, payment_type: e.target.value})}
                      className="input-field"
                      disabled={loading}
                    >
                      <option value="full">Full Payment</option>
                      <option value="installment">Installment Payment</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Course Fee (₦)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editStudent.total_fee}
                      onChange={(e) => setEditStudent({...editStudent, total_fee: e.target.value})}
                      className="input-field"
                      placeholder="0.00"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid (₦)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editStudent.amount_paid}
                      onChange={(e) => setEditStudent({...editStudent, amount_paid: e.target.value})}
                      className="input-field"
                      placeholder="0.00"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_payment_confirmed"
                      checked={editStudent.payment_confirmed}
                      onChange={(e) => setEditStudent({...editStudent, payment_confirmed: e.target.checked})}
                      className="mr-2"
                      disabled={loading}
                    />
                    <label htmlFor="edit_payment_confirmed" className="text-sm font-medium text-gray-700">
                      Payment Confirmed
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 btn-primary disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Student'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEditCourse(null);
                    setEditCourseSearchTerm('');
                    setShowEditCourseDropdown(false);
                  }}
                  className="flex-1 btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Delete Student</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={loading}
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-red-100 mr-4">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Are you sure?</h4>
                  <p className="text-sm text-gray-600">You are about to delete this student</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-medium">
                      {selectedStudent.full_name ? selectedStudent.full_name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'ST'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedStudent.full_name}</p>
                    <p className="text-sm text-gray-600">{selectedStudent.phone}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h5 className="font-medium text-red-800 mb-1">Warning</h5>
                    <p className="text-sm text-red-700">
                      This action cannot be reversed. All student data, including enrollment history and progress, will be permanently deleted.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteStudent}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Student'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement; 